// =============================================================================
// onCallRoster.js — on-call duties are identified by (date, role), not by id
// =============================================================================
// Ids have come in several formats: `oc_{date}_{roleIndex}` from this branch's
// solver, `oc_{date}_manual_{timestamp}` from a hand-assigned slot, and
// `{date}_{role}` from the `main` branch. Merging by id left two holders for one
// night whenever two formats met (a manual assignment followed by a solve, or a
// solve over a period published elsewhere). The server's publish now replaces by
// (date, role) too, so both sides agree on what "the same duty" means.
// =============================================================================

// The roles are the department's own (Settings -> On-call roles; the server's
// src/logic/on_call_roles.py). These four are what a department that has set
// none gets -- the same default the solver uses. A duty's `role` is the role's
// KEY (Oct 2026), which never changes; `name` is what is shown.
export const DEFAULT_ON_CALL_ROLES = [
  { key: 'consultant', name: 'Consultant', grade: 0, site: 'NUH', site_staff_only: false },
  { key: 'senior_resident', name: 'Senior Resident', grade: 1, site: 'NUH', site_staff_only: false },
  { key: 'mo_nuh', name: 'MO (NUH)', grade: 2, site: 'NUH', site_staff_only: true },
  { key: 'mo_ntfgh', name: 'MO (NTFGH)', grade: 2, site: 'NTFGH', site_staff_only: false },
];
export const GRADE_LABELS = { 0: 'Consultant', 1: 'Senior Resident', 2: 'Junior' };

/** The department's on-call roles, from the schedule payload. */
export function rolesOf(schedule) {
  const roles = schedule?.onCallRoles || schedule?.on_call_roles;
  return Array.isArray(roles) && roles.length ? roles : DEFAULT_ON_CALL_ROLES;
}

/** A role's display name from its key; the key itself for a role no longer
 *  configured (a removed role's past duties keep their key). */
export function roleName(roles, key) {
  return (roles || []).find(r => r.key === key)?.name || key || '';
}

/** Whether somebody of this grade and institution may take the role -- the
 *  solver's own test: the role's grade, and for a site-staff-only role no
 *  OTHER institution recorded (somebody with none recorded may). */
export function mayTake(role, grade, institution) {
  if (!role || grade !== role.grade) return false;
  if (role.site_staff_only && institution && institution !== role.site) return false;
  return true;
}

/** A short tag for a role in a narrow cell: "Senior Resident" -> "SR",
 *  "MO (NTFGH)" -> "NTFGH", "Consultant" -> "C". */
export function roleTag(name) {
  const inBrackets = /\(([^)]+)\)/.exec(name || '');
  if (inBrackets) return inBrackets[1];
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  return words.length > 1 ? words.map(w => w[0]).join('').toUpperCase() : (words[0] || '?').slice(0, 1).toUpperCase();
}

const dateOf = (s) => String(s?.date || '').slice(0, 10);

/** The key that identifies one night's duty. */
export const slotKey = (s) => `${dateOf(s)}|${s?.role}`;

/**
 * The id a duty should carry: the solver's own format, so a hand-assigned slot
 * and a solved one are the same record rather than two.
 */
export function onCallId(date, role, roles = DEFAULT_ON_CALL_ROLES) {
  const idx = roles.findIndex(r => r.key === role);
  return `oc_${String(date).slice(0, 10)}_${idx >= 0 ? idx : role}`;
}

/** `prev` with every duty in `incoming` replacing the one for the same night and role. */
export function mergeOnCall(prev = [], incoming = []) {
  const replaced = new Set((incoming || []).map(slotKey));
  return [...(prev || []).filter((s) => !replaced.has(slotKey(s))), ...(incoming || [])];
}
